import { ButtonList, DropdownButton } from '@dixa/lib-shared-habla';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { useCallback } from 'react';

import itemStyles from './numberTableItem.module.scss';

type Props = {
  onEdit: VoidFunction;
  onRename: VoidFunction;
  onAudit: VoidFunction;
  onDuplicate: VoidFunction;
  onMove: VoidFunction;
  onRemove: VoidFunction;
  isActive: boolean;
};

const NumberTableFlowsDropdown = ({
  onEdit,
  onRename,
  onDuplicate,
  onAudit,
  onMove,
  onRemove,
  isActive,
}: Props): JSX.Element => {
  const hasChannelSetupAccess = useHasAccess(
    'settings.integration.channel.setup',
    { fallbackRole: 'admin' },
  );
  const hasFlowWriteAccess = useHasAccess('settings.flow:w');

  const onToggleMenu = useCallback((isOpen: boolean) => {
    if (isOpen) {
      ampli.phoneFlowOptionsMenuOptionOpened();
    }
  }, []);

  const onEditClick = useCallback(() => {
    ampli.phoneFlowOptionsMenuOptionEditClicked();
    onEdit();
  }, [onEdit]);

  const onDuplicateClick = useCallback(() => {
    ampli.phoneFlowOptionsMenuOptionDuplicateClicked();
    onDuplicate();
  }, [onDuplicate]);

  const onRenameClick = useCallback(() => {
    ampli.phoneFlowOptionsMenuOptionRenameClicked();
    onRename();
  }, [onRename]);

  const onMoveToAnotherNumberClick = useCallback(() => {
    ampli.phoneFlowOptionsMenuOptionMoveToAnotherNumberClicked();
    onMove();
  }, [onMove]);

  const onAuditClick = useCallback(() => {
    ampli.phoneFlowOptionsMenuOptionAuditLogClicked();
    onAudit();
  }, [onAudit]);

  const onRemoveClick = useCallback(() => {
    ampli.phoneFlowOptionsMenuOptionDeleteClicked();
    onRemove();
  }, [onRemove]);

  return (
    <DropdownButton
      className={itemStyles.dropdown}
      icon="dots-h"
      fixed
      buttonProps={{ variant: 'tertiary' }}
      onToggle={onToggleMenu}
      noArrow
      dropdown={
        <ButtonList>
          {hasFlowWriteAccess && (
            <ButtonList.Item onClick={onEditClick}>Edit</ButtonList.Item>
          )}

          {hasFlowWriteAccess && hasChannelSetupAccess && (
            <ButtonList.Item onClick={onDuplicateClick}>
              Duplicate
            </ButtonList.Item>
          )}

          {hasFlowWriteAccess && (
            <ButtonList.Item onClick={onRenameClick}>Rename</ButtonList.Item>
          )}

          {hasFlowWriteAccess && hasChannelSetupAccess && (
            <ButtonList.Item
              onClick={onMoveToAnotherNumberClick}
              /*
               * These actions should be disabled if the flow is active,
               * because backend wouldn't allow us to move them / delete them anyways.
               */
              isDisabled={isActive}
            >
              Move to another number
            </ButtonList.Item>
          )}

          <ButtonList.Item onClick={onAuditClick}>
            See audit log
          </ButtonList.Item>

          {hasFlowWriteAccess && hasChannelSetupAccess && (
            <>
              <ButtonList.Divider />
              <ButtonList.Item onClick={onRemoveClick} isDisabled={isActive}>
                Delete
              </ButtonList.Item>
            </>
          )}
        </ButtonList>
      }
    />
  );
};

export default NumberTableFlowsDropdown;
